import { useInfiniteQuery } from '@serodigital/hooks';
import { FilterV2Button, InfiniteTable, SearchField, withInfiniteTable } from '@serodigital/ui-components';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { useMemo, useState, useEffect, useRef } from 'react';
import {
  GetConfigProfileVersions,
  GetConfigProfileVersionsQuery,
  GetFirmwareVersions,
  GetFirmwareVersionsQuery,
  ListTrials,
  ListTrialsQuery,
  SearchBees,
  Bee,
  GetBeeModes,
  GetBeeModesQuery,
} from '../../../shared/api-clients/gql';
import { useDebounce } from '../../../shared/hooks/useDebounce';
import { ListFilter } from '../views/home/beesList/ListFilter';
import { useListBees } from '../contexts/listBees';
import { useListFilter } from '../contexts/listFilter';
import { getWindowDimensions } from '../helpers/windowDimensions';
import { ITrialDetail } from '../views/bee/state/types';
import { FirmwareUpgrade } from '../views/home/beesList/firmwareUpgrade';
import { useTableDefinition } from './useTableDefinition';
import { useGraphQLQuery } from '../helpers/graphQLDataFetcher';

const Table = () => {
  const resetDebounce = useDebounce(250);
  const { setSelectedBees, selectedBees, showCheckboxes, selectedHardwareVersion } = useListBees();

  const { setDropdownFilters, filterOption, filterCount, setFilter, setFilterOption, queryOptions, filter } =
    useListFilter();

  const { push } = useHistory();
  const { generateTableDefinition } = useTableDefinition();

  const { data: trialsData } = useGraphQLQuery<ListTrialsQuery>(ListTrials);
  const { data: configProfileVersionsData } = useGraphQLQuery<GetConfigProfileVersionsQuery>(GetConfigProfileVersions);
  const { data: firmwareVersionsData } = useGraphQLQuery<GetFirmwareVersionsQuery>(GetFirmwareVersions);
  const { data: beeModeData } = useGraphQLQuery<GetBeeModesQuery>(GetBeeModes);

  const ref = useRef(null);

  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  const [isOpen, setIsOpen] = useState(false);

  const [trials, setTrials] = useState<ITrialDetail[]>([]);

  const [configProfileVersions, setConfigProfileVersions] = useState<string[]>([]);

  const [firmwareVersions, setFirmwareVersions] = useState<string[]>([]);

  const [beeMode, setBeeMode] = useState<string[]>([]);

  const { onNextPage } = useInfiniteQuery(
    SearchBees,
    { pageSize: 50, dataKey: 'searchBees', disableSortOptions: true, queryHookOptions: {}, defaultOffset: 0 },
    queryOptions,
  );

  useEffect(() => {
    if (!trialsData || trials.length > 0) return;
    setTrials(
      trialsData.listTrials.items.map((trial) => ({
        id: trial.id,
        name: trial.name,
      })),
    );
  }, [trialsData && onNextPage]);

  useEffect(() => {
    if (!configProfileVersionsData || configProfileVersions.length > 0) return;
    setConfigProfileVersions(configProfileVersionsData?.getConfigProfileVersions?.configProfileVersions);
  }, [configProfileVersionsData && onNextPage]);

  useEffect(() => {
    if (!firmwareVersionsData || firmwareVersions.length > 0) return;
    setFirmwareVersions(firmwareVersionsData.getFirmwareVersions.firmwareVersions);
  }, [firmwareVersionsData && onNextPage]);

  useEffect(() => {
    if (!beeModeData || beeMode.length > 0) return;
    setBeeMode(beeModeData.getBeeModes.beeModes);
  }, [beeModeData && onNextPage]);

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions(getWindowDimensions());
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  const columns = useMemo(() => {
    return generateTableDefinition(
      setSelectedBees,
      selectedBees,
      showCheckboxes,
      selectedHardwareVersion,
      windowDimensions?.width,
    );
  }, [windowDimensions?.width, selectedBees, showCheckboxes, selectedHardwareVersion]);

  const viewDetails = (_e: React.MouseEvent, bee: Bee) => {
    push(`/bee/${bee.deviceId}/details`, { ...bee });
  };

  return (
    <div ref={ref} className="pt-20 flex-1 mx-72" data-qa="list-bees">
      <div className="flex justify-center mb-2">
        <SearchField
          data-qa="list-bees-search"
          className="sero-mb-2"
          placeholder="Search Table"
          onChange={(query) => {
            resetDebounce(() => setFilter(query.target.value));
          }}
          defaultValue={filter}
        />
        <FilterV2Button
          className="mx-4"
          count={filterCount}
          isOpen={isOpen}
          onToggle={() => setIsOpen((prev) => !prev)}
          data-qa={'filter-toggle-button'}
        />
      </div>
      {isOpen ? (
        <ListFilter
          setFilterOption={setFilterOption}
          setDropdownFilters={setDropdownFilters}
          filterOption={filterOption}
          shouldDisableClearButton={filterCount === 0}
          dynamicDropDownValues={{
            trials: trials,
            configProfileVersions: configProfileVersions,
            firmwareVersions: firmwareVersions,
            beeModes: beeMode,
          }}
        />
      ) : null}
      {selectedBees?.length ? <FirmwareUpgrade selectedBees={selectedBees} /> : null}
      <InfiniteTable data-qa="list-bees-table" columns={columns} onLoadNextPage={onNextPage} onClick={viewDetails} />
    </div>
  );
};

export const ListBeesTable = withInfiniteTable(Table);
